import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Edit2,
  Copy,
  Trash2,
  Archive,
  Calendar,
  RefreshCw,
  CheckSquare,
  Square,
  MinusSquare,
  CheckCircle,
  XCircle,
  X,
} from 'lucide-react';
import { api, type SMCCContent } from '../../../services/api';
import {
  PLATFORM_CONFIG,
  CONTENT_STATUSES,
  CONTENT_TYPES,
  getStatusColors,
  type SMCCPlatformId,
} from '../../../constants/smcc';

// Derive STATUS_CONFIG from CONTENT_STATUSES for backward compatibility
const STATUS_CONFIG: Record<string, { color: string; bg: string; label: string }> = {};
for (const status of CONTENT_STATUSES) {
  const colors = getStatusColors(status.value);
  STATUS_CONFIG[status.value] = {
    color: colors.text,
    bg: colors.bg,
    label: status.label,
  };
}


function ContentRow({
  item,
  isSelected,
  onSelect,
  onView,
  onEdit,
  onDuplicate,
  onArchive,
  onDelete,
}: {
  item: SMCCContent;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
  onView: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.draft;

  return (
    <tr className={`hover:bg-gray-50 ${isSelected ? 'bg-primary-50' : ''}`}>
      <td className="px-4 py-3 w-12">
        <button
          onClick={() => onSelect(!isSelected)}
          className="p-1 text-gray-400 hover:text-gray-600"
          aria-label={isSelected ? 'Deselect item' : 'Select item'}
        >
          {isSelected ? (
            <CheckSquare className="w-5 h-5 text-primary-600" />
          ) : (
            <Square className="w-5 h-5" />
          )}
        </button>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex -space-x-1">
            {item.target_platforms?.slice(0, 3).map((platform) => (
              <div
                key={platform}
                className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-white text-[10px] font-bold"
                style={{ backgroundColor: PLATFORM_CONFIG[platform as SMCCPlatformId]?.color || '#666' }}
              >
                {platform[0].toUpperCase()}
              </div>
            ))}
            {(item.target_platforms?.length || 0) > 3 && (
              <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-gray-600 text-[10px] font-bold">
                +{item.target_platforms.length - 3}
              </div>
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900 line-clamp-1">
              {item.title || item.body?.slice(0, 50) || 'Untitled'}
            </p>
            <p className="text-xs text-gray-500 capitalize">{item.content_type}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}
        >
          {statusConfig.label}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {item.scheduled_at
          ? new Date(item.scheduled_at).toLocaleDateString()
          : item.published_at
          ? new Date(item.published_at).toLocaleDateString()
          : '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {new Date(item.created_at).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
                onKeyDown={(e) => e.key === 'Escape' && setShowMenu(false)}
                role="button"
                tabIndex={0}
                aria-label="Close menu"
              />
              <div className="absolute right-0 top-8 z-20 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                <button
                  onClick={() => {
                    onView();
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
                <button
                  onClick={() => {
                    onEdit();
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onDuplicate();
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Duplicate
                </button>
                {item.status !== 'archived' && (
                  <button
                    onClick={() => {
                      onArchive();
                      setShowMenu(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Archive className="w-4 h-4" />
                    Archive
                  </button>
                )}
                {item.status !== 'published' && (
                  <>
                    <hr className="my-1" />
                    <button
                      onClick={() => {
                        if (confirm('Delete this content?')) {
                          onDelete();
                        }
                        setShowMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function SMCCContentList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkActionMessage, setBulkActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch content
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['smcc-content', search, statusFilter, typeFilter],
    queryFn: () =>
      api.getSMCCContent({
        search: search || undefined,
        status: statusFilter || undefined,
        content_type: typeFilter || undefined,
        limit: 50,
      }),
  });

  // Query key for current filters
  const queryKey = ['smcc-content', search, statusFilter, typeFilter];

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: (id: string) => api.duplicateSMCCContent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-content'] });
    },
  });

  // Archive mutation with optimistic update
  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.archiveSMCCContent(id),
    onMutate: async (id: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });
      // Snapshot previous value
      const previous = queryClient.getQueryData(queryKey);
      // Optimistically update - change status to archived
      queryClient.setQueryData(queryKey, (old: { content: SMCCContent[]; total: number } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          content: old.content.map((item) =>
            item.id === id ? { ...item, status: 'archived' } : item
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-content'] });
    },
  });

  // Delete mutation with optimistic update
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteSMCCContent(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      // Optimistically remove from list
      queryClient.setQueryData(queryKey, (old: { content: SMCCContent[]; total: number } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          content: old.content.filter((item) => item.id !== id),
          total: old.total - 1,
        };
      });
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-content'] });
    },
  });

  const content = data?.content || [];
  const total = data?.total || 0;

  // Selection helpers
  const isAllSelected = content.length > 0 && content.every((item: SMCCContent) => selectedIds.has(item.id));
  const isSomeSelected = content.some((item: SMCCContent) => selectedIds.has(item.id)) && !isAllSelected;
  const selectedCount = selectedIds.size;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(content.map((item: SMCCContent) => item.id)));
    }
  };

  const toggleSelect = (id: string, selected: boolean) => {
    const newSet = new Set(selectedIds);
    if (selected) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    setSelectedIds(newSet);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Bulk action handlers with optimistic updates
  const handleBulkArchive = async () => {
    if (!confirm(`Archive ${selectedCount} items?`)) return;

    setBulkActionLoading(true);
    const ids = Array.from(selectedIds);
    const count = selectedCount;

    // Optimistic update - mark as archived immediately
    await queryClient.cancelQueries({ queryKey });
    const previousData = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, (old: { content: SMCCContent[]; total: number } | undefined) => {
      if (!old) return old;
      return {
        ...old,
        content: old.content.map((item) =>
          ids.includes(item.id) ? { ...item, status: 'archived' } : item
        ),
      };
    });
    clearSelection();

    try {
      await Promise.all(ids.map(id => api.archiveSMCCContent(id)));
      setBulkActionMessage({ type: 'success', text: `${count} items archived` });
    } catch {
      // Rollback on error
      queryClient.setQueryData(queryKey, previousData);
      setBulkActionMessage({ type: 'error', text: 'Failed to archive some items' });
    } finally {
      setBulkActionLoading(false);
      queryClient.invalidateQueries({ queryKey: ['smcc-content'] });
      setTimeout(() => setBulkActionMessage(null), 3000);
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedCount} items? This cannot be undone.`)) return;

    setBulkActionLoading(true);
    const ids = Array.from(selectedIds);
    const count = selectedCount;

    // Optimistic update - remove from list immediately
    await queryClient.cancelQueries({ queryKey });
    const previousData = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, (old: { content: SMCCContent[]; total: number } | undefined) => {
      if (!old) return old;
      return {
        ...old,
        content: old.content.filter((item) => !ids.includes(item.id)),
        total: old.total - ids.length,
      };
    });
    clearSelection();

    try {
      await Promise.all(ids.map(id => api.deleteSMCCContent(id)));
      setBulkActionMessage({ type: 'success', text: `${count} items deleted` });
    } catch {
      // Rollback on error
      queryClient.setQueryData(queryKey, previousData);
      setBulkActionMessage({ type: 'error', text: 'Failed to delete some items' });
    } finally {
      setBulkActionLoading(false);
      queryClient.invalidateQueries({ queryKey: ['smcc-content'] });
      setTimeout(() => setBulkActionMessage(null), 3000);
    }
  };

  const handleBulkApprove = async () => {
    if (!confirm(`Approve ${selectedCount} items?`)) return;

    setBulkActionLoading(true);
    const ids = Array.from(selectedIds);
    const count = selectedCount;

    // Optimistic update - mark as approved immediately
    await queryClient.cancelQueries({ queryKey });
    const previousData = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, (old: { content: SMCCContent[]; total: number } | undefined) => {
      if (!old) return old;
      return {
        ...old,
        content: old.content.map((item) =>
          ids.includes(item.id) ? { ...item, status: 'approved' } : item
        ),
      };
    });
    clearSelection();

    try {
      await Promise.all(ids.map(id => api.approveSMCCContent(id)));
      setBulkActionMessage({ type: 'success', text: `${count} items approved` });
    } catch {
      // Rollback on error
      queryClient.setQueryData(queryKey, previousData);
      setBulkActionMessage({ type: 'error', text: 'Failed to approve some items' });
    } finally {
      setBulkActionLoading(false);
      queryClient.invalidateQueries({ queryKey: ['smcc-content'] });
      setTimeout(() => setBulkActionMessage(null), 3000);
    }
  };

  const handleBulkReject = async () => {
    const reason = prompt(`Reject ${selectedCount} items?\n\nEnter rejection reason (optional):`);
    if (reason === null) return; // User cancelled

    setBulkActionLoading(true);
    const ids = Array.from(selectedIds);
    const count = selectedCount;

    // Optimistic update - mark as rejected immediately
    await queryClient.cancelQueries({ queryKey });
    const previousData = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, (old: { content: SMCCContent[]; total: number } | undefined) => {
      if (!old) return old;
      return {
        ...old,
        content: old.content.map((item) =>
          ids.includes(item.id) ? { ...item, status: 'rejected' } : item
        ),
      };
    });
    clearSelection();

    try {
      await Promise.all(ids.map(id => api.rejectSMCCContent(id, reason || 'Rejected via bulk action')));
      setBulkActionMessage({ type: 'success', text: `${count} items rejected` });
    } catch {
      // Rollback on error
      queryClient.setQueryData(queryKey, previousData);
      setBulkActionMessage({ type: 'error', text: 'Failed to reject some items' });
    } finally {
      setBulkActionLoading(false);
      queryClient.invalidateQueries({ queryKey: ['smcc-content'] });
      setTimeout(() => setBulkActionMessage(null), 3000);
    }
  };

  return (
    <div className="space-y-6" role="main" aria-label="Content Library">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" id="content-list-title">Content Library</h1>
          <p className="text-gray-500 mt-1">Manage all your social media content</p>
        </div>
        <button
          onClick={() => navigate('/admin/smcc/content/new')}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          aria-label="Create new content"
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
          <span>Create Content</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4" role="search" aria-label="Search and filter content">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search content..."
              aria-label="Search content"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
            aria-controls="filter-panel"
            className={`px-4 py-2 rounded-lg border flex items-center gap-2 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              showFilters || statusFilter || typeFilter
                ? 'bg-primary-50 border-primary-200 text-primary-600'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {(statusFilter || typeFilter) && (
              <span className="bg-primary-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {(statusFilter ? 1 : 0) + (typeFilter ? 1 : 0)}
              </span>
            )}
          </button>
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="smcc-status-filter" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                id="smcc-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All statuses</option>
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <option key={value} value={value}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="smcc-type-filter" className="block text-sm font-medium text-gray-700 mb-1">Content Type</label>
              <select
                id="smcc-type-filter"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All types</option>
                {CONTENT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Action Toolbar */}
      {selectedCount > 0 && (
        <div
          className="bg-primary-50 border border-primary-200 rounded-xl p-4 flex items-center justify-between"
          role="toolbar"
          aria-label={`Bulk actions for ${selectedCount} selected items`}
        >
          <div className="flex items-center gap-4">
            <span className="font-medium text-primary-700" aria-live="polite">
              {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={clearSelection}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
              aria-label="Clear selection"
            >
              <X className="w-4 h-4" aria-hidden="true" />
              <span>Clear</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkApprove}
              disabled={bulkActionLoading}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm flex items-center gap-1.5 focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              aria-label={`Approve ${selectedCount} selected items`}
            >
              <CheckCircle className="w-4 h-4" aria-hidden="true" />
              <span>Approve</span>
            </button>
            <button
              onClick={handleBulkReject}
              disabled={bulkActionLoading}
              className="px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm flex items-center gap-1.5 focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
              aria-label={`Reject ${selectedCount} selected items`}
            >
              <XCircle className="w-4 h-4" aria-hidden="true" />
              <span>Reject</span>
            </button>
            <button
              onClick={handleBulkArchive}
              disabled={bulkActionLoading}
              className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm flex items-center gap-1.5 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              aria-label={`Archive ${selectedCount} selected items`}
            >
              <Archive className="w-4 h-4" aria-hidden="true" />
              <span>Archive</span>
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm flex items-center gap-1.5 focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              aria-label={`Delete ${selectedCount} selected items`}
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
              <span>Delete</span>
            </button>
            {bulkActionLoading && (
              <RefreshCw className="w-4 h-4 text-primary-600 animate-spin" aria-label="Loading" />
            )}
          </div>
        </div>
      )}

      {/* Bulk Action Messages */}
      {bulkActionMessage && (
        <div
          className={`p-4 rounded-lg flex items-center gap-2 ${
            bulkActionMessage.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
          role="alert"
        >
          {bulkActionMessage.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          {bulkActionMessage.text}
        </div>
      )}

      {/* Content Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : content.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">No content found</p>
            <button
              onClick={() => navigate('/admin/smcc/content/new')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Create Content
            </button>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left w-12">
                    <button
                      onClick={toggleSelectAll}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      aria-label={isAllSelected ? 'Deselect all' : 'Select all'}
                    >
                      {isAllSelected ? (
                        <CheckSquare className="w-5 h-5 text-primary-600" />
                      ) : isSomeSelected ? (
                        <MinusSquare className="w-5 h-5 text-primary-600" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Content
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-12">

                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {content.map((item: SMCCContent) => (
                  <ContentRow
                    key={item.id}
                    item={item}
                    isSelected={selectedIds.has(item.id)}
                    onSelect={(selected) => toggleSelect(item.id, selected)}
                    onView={() => navigate(`/admin/smcc/content/${item.id}`)}
                    onEdit={() => navigate(`/admin/smcc/content/${item.id}/edit`)}
                    onDuplicate={() => duplicateMutation.mutate(item.id)}
                    onArchive={() => archiveMutation.mutate(item.id)}
                    onDelete={() => deleteMutation.mutate(item.id)}
                  />
                ))}
              </tbody>
            </table>

            {/* Pagination Info */}
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {content.length} of {total} items
                {selectedCount > 0 && (
                  <span className="ml-2 text-primary-600">
                    ({selectedCount} selected)
                  </span>
                )}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
